import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EventEmitter } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../../services/config.service';
import { WorkflowUtilitiesService } from '../services/workflow-utilities.service';
import { formatString } from '../../utils/string-utils';

@Injectable({
	providedIn: 'root'
})
export class BatchService {

	// EventEmitter used to replace AngularJS $broadcast (eg. $rootScope.$broadcast()), "dxBatchEvent"
	workflowBatchEvent: EventEmitter<{ eventType: string, timestamp: Date }> = new EventEmitter();

	downloadJobTypes = [
		{ id: 'result', label: 'Result', format: 'pdf', clazz: 'btn-primary' },
		//    	{id: 'annotation', label: 'Annotations', format: 'xml', clazz: 'btn-info'},
		//    	{id: 'history', label: 'Annotation History', format: 'zip', clazz: 'btn-info'},    	
		//    	{id: 'source', label: 'Original Document', format: 'pdf', clazz: 'btn-success'},
		//    	{id: 'all', label: 'All Assets', format: 'zip', clazz: 'btn-warning'},
		{ id: 'results', label: 'Result and Annotations', format: 'zip', clazz: 'btn-success' }
	];

	constructor(
		private dxUtilsService: WorkflowUtilitiesService,
		private configService: ConfigService,
		private http: HttpClient
	) { }

	async getBatches(page: number = 1, paging: any = {}, constraints: any = {}): Promise<any> {
		let url = this.configService.getConfig().dx.batchesURL;
		url += `?page=${page}`;
		url += `&size=${paging.elementsPerPage}`;
		url += `&sort=${paging.sorting.sort}`;
		url += `&constraints=${encodeURIComponent(JSON.stringify(constraints))}`;

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));

			if (response?.content) {
				let batches = response.content;
				batches.forEach((batch: any) => {
					this.formatBatch(batch, false);
				});
				return response;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async createBatch(payload: any): Promise<any> {
		let url = this.configService.getConfig().dx.batchesURL;
		url += `/add`;

		try {
			return await firstValueFrom(this.http.post<any>(url, payload));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getBatch(id: number): Promise<any> {
		let url = this.configService.getConfig().dx.batchesURL;
		url += `/${id}`;

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));

			if (response) {
				let batch = response;
				this.formatBatch(batch, true);
				return batch;
			} else {
				return Promise.reject(false);
			}
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async uniqueBatchNameTest(name: string): Promise<any> {
		let url = this.configService.getConfig().dx.batchesURL;
		url += `/unique/name/${encodeURIComponent(JSON.stringify(name))}`;

		try {
			const response = await firstValueFrom(this.http.get<any>(url, {}));
			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	private formatBatch(batch?: any, full: boolean = false): void {
		if (!batch) return;

		let created = new Date(batch.createdDate);
		batch.createdDateStr = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');

		batch.dueDateStr = "";
		if (batch.dueDate) {
			let due = new Date(batch.dueDate);
			batch.dueDateStr = due.toLocaleDateString('en-US') + ' ' + due.toLocaleTimeString('en-US');
		}

		if (!batch.parameters) batch.parameters = [];
		batch.hasQueries = false;
		batch.parameters.forEach((p: any) => {
			if (p.type == "QUERY") {
				if (p.values && p.values.query_ids != null || p.values.query_list_ids != null) {
					batch.hasQueries = true;
				}
			}
		});

		let batch_comments: any[] = [];
		if (batch.comments && batch.comments.comments && batch.comments.comments.length > 0) {
			batch.comments.comments.forEach((c: any) => {
				let created = new Date(c.commentDate);
				c.commentDateStr = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');
				batch_comments.push(c);
			});
			delete batch.comments;
			batch.comments = batch_comments;
		}

		// Custom metadata processing
		batch.batchMetadataDisplay = [];
		let metadataFields: { [key: string]: any } = this.dxUtilsService.getDomainCustomization("metadata") || {};
		let metadataFieldsDisplay = metadataFields['display'] || [];

		let batchMetadataParameters = batch.parameters.find(function(p: any) {
			return p.name == "batch_metadata";
		});
		let metadataValues: any = {};
		if (batchMetadataParameters) {
			try {
				metadataValues = JSON.parse(batchMetadataParameters.values["stringValue"]);
			} catch (err) { }
		}
		metadataFieldsDisplay.forEach((f: any) => {
			if (f.isBatchView) {
				let fieldvalue = metadataValues[f.field];
				if (fieldvalue && fieldvalue.length > 0) {
					batch.batchMetadataDisplay.push({ field: f.field, label: f.label, value: fieldvalue.join(', ') });
				} else {
					batch.batchMetadataDisplay.push({ field: f.field, label: f.label, value: "" });
				}
			}
		});
		// END Custom metadata processing

		if (batch.documents) {
			batch.documents.forEach((d: any) => {
				if (d.comments && d.comments.comments && d.comments.comments.length > 0) {
					let doc_comments: any[] = [];

					d.comments.comments.forEach((c: any) => {
						let created = new Date(c.commentDate);
						c.commentDateStr = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');
						doc_comments.push(c);
					});
					delete d.comments;
					d.comments = doc_comments;
				}

				// Custom metadata processing
				d.productMetadataDisplay = [];
				let metadataFields: { [key: string]: any } = this.dxUtilsService.getDomainCustomization("metadata") || {};
				let metadataFieldsDisplay = metadataFields['display'] || [];
				let metadataValues = d.productMetadata || {};
				metadataFieldsDisplay.forEach((f: any) => {
					let fieldvalue = metadataValues[f.field];
					if (fieldvalue && fieldvalue.length > 0) {
						d.productMetadataDisplay.push({ field: f.field, label: f.label, value: fieldvalue.join(', ') });
					} else {
						d.productMetadataDisplay.push({ field: f.field, label: f.label, value: "" });
					}
				});
				// END Custom metadata processing     			

				if (full) {
					// format any task statistics
					if (!d.declassTaskData) d.declassTaskData = { dataSet: [] };
					this.formatStats(d.declassTaskData);
				}
			});
		}
		if (full && batch.queries) {
			batch.queries.forEach((q: any) => {
				if (q.queryListNames) {
					q.queryListNamesStr = q.queryListNames.join();
				}
			});
		}

		// determine the available batch actions based on current batch state:
		// INITIAL,IN_PROGRESS,COMPLETED,ERROR,ABORTED,DELETED
		// see com.ptfs.aware.services.declassification.model.BatchState
		batch.actions = {
			canRetry: false,
			canAbort: false,
			canDelete: false
		};
		if ('INITIAL' == batch.state || 'IN_PROGRESS' == batch.state) {
			batch.actions.canAbort = true;
		} else if ('COMPLETED' == batch.state || 'ERROR' == batch.state || 'ABORTED' == batch.state) {
			batch.actions.canDelete = true;
		}
		if ('ERROR' == batch.state) {
			batch.actions.canRetry = true;
		}

		batch.stateTotals = "";
		//    	let totalSearching = 0;
		//    	let totalDocs = batch.documents ? batch.documents.length : 0;
		//		angular.forEach(batch.documents, function(d, idx) {
		//			d.stateStr = d.state;
		//			if ('INITIAL' == d.state) {
		//				d.stateStr = 'SEARCHING';
		//				totalSearching++;
		//			}
		//		});
		//		if (totalSearching > 0) {
		//			batch.stateTotals = formatString("(Searching {0} of {1})", totalSearching, totalDocs);
		//		}		
	}

	async getWorkflows(): Promise<any> {
		let url = this.configService.getConfig().dx.workflowsURL;

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async startWorkflow(id: number): Promise<any> {
		let url = this.configService.getConfig().dx.batchesURL;
		url += `/workflow/start/${id}`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, {}));
			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getPriorities(): Promise<any> {
		let url = this.configService.getConfig().dx.prioritiesURL;

		try {
			return await firstValueFrom(this.http.get<any>(url, {}));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async massAbortBatches(payload: any): Promise<any> {
		let url = this.configService.getConfig().dx.batchesURL;
		url += `/mass/abort`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, payload));

			this.workflowBatchEvent.emit({
				eventType: 'abort',
				timestamp: new Date(),
			});

			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async massDeleteBatches(payload: any): Promise<any> {
		let url = this.configService.getConfig().dx.batchesURL;
		url += `/mass/delete`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, payload));

			this.workflowBatchEvent.emit({
				eventType: 'delete',
				timestamp: new Date(),
			});

			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async massRetryBatches(payload: any): Promise<any> {
		let url = this.configService.getConfig().dx.batchesURL;
		url += `/mass/retry`;

		try {
			const response = await firstValueFrom(this.http.post<any>(url, payload));

			this.workflowBatchEvent.emit({
				eventType: 'retry',
				timestamp: new Date(),
			});

			return Promise.resolve(true);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	/**
	 * Downloads the results for a specific batch document job.
	 */
	async downloadJobResults(jobid: number, downloadJobType: any): Promise<any> {
		let url = formatString(this.configService.getConfig().dx.redactedResultURL, downloadJobType.id, jobid);

		try {
			return await firstValueFrom(this.http.get(url, { responseType: 'arraybuffer' }));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	/**
	 * Downloads the results for all documents in the batch.
	 */
	async downloadBatchResults(batchid: number): Promise<any> {
		let url = this.configService.getConfig().dx.workflowResultsURL;
		url += `/batch/result/${batchid}`;

		try {
			return await firstValueFrom(this.http.get(url, { responseType: 'arraybuffer' }));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async getProcessDiagram(processid: number): Promise<any> {
		let url = this.configService.getConfig().dx.baseWorkflowURL;
		url += `/services/images/process/${processid}`;

		try {
			// This call returns an XML representation of the workflow process diagram
			return await firstValueFrom(this.http.get(url, { responseType: 'text' }));
		} catch (error) {
			return Promise.reject(error);
		}
	}

	private getTime(milli: number): string {
		if (!milli) return "";
		var time = new Date(milli);
		var hours = time.getUTCHours();
		var minutes = time.getUTCMinutes();
		var seconds = time.getUTCSeconds();
		return hours + ":" + minutes + ":" + seconds;
	}

	// FIXME: have another look at  declassService.getTaskStatisticsForDocument(). A document
	// can have multiple tasks, and each task should have task data and statistic properties.
	// For now, we're just using one task per document.
	private formatStats(stat?: any): void {
		if (!stat) return;
		// let customize = this.getCustomization();

		stat.taskDurationString = this.getTime(stat.taskDuration);

		// Custom metadata processing
		stat.productMetadataDisplay = [];
		let metadataFields: { [key: string]: any } = this.dxUtilsService.getDomainCustomization("metadata") || {};
		let metadataFieldsDisplay = metadataFields['display'] || [];
		let metadataValues = stat.productMetadata || {};
		metadataFieldsDisplay.forEach((f: any) => {
			let fieldvalue = metadataValues[f.field];
			if (fieldvalue && fieldvalue.length > 0) {
				stat.productMetadataDisplay.push({ field: f.field, label: f.label, value: fieldvalue.join(', ') });
			} else {
				stat.productMetadataDisplay.push({ field: f.field, label: f.label, value: "" });
			}
		});
		// END Custom metadata processing     	

		let dataset = [];
		if (stat.taskData) {
			dataset = stat.taskData.dataSet;
		} else if (stat.dataSet) {
			dataset = stat.dataSet;
		}
		// capture the decision processed by the completion form
		// FIXME: we're just adding everything to the overall stat object for now
		// but need to eventually break it out into an array of stats for each task and 
		// calculate a final result (finalResult vs taskResult)
		let taskDataForm = null;  // taskDataForm = customize.taskDataForm ? customize.taskDataForm.name : null;
		if ('sf715TaskData' == taskDataForm) {
			dataset.forEach((ds: any) => {
				let jsondata = JSON.parse(ds.data);
				stat.reviewDate = jsondata.reviewDate;

				if (jsondata.declassify) {
					stat.decision = "Declassify";
					stat.reason = "";
				}
				else if (jsondata.exempt) {
					stat.decision = "Exempt";
					stat.reason = jsondata.exemptReason || "";
				}
				else if (jsondata.exclude) {
					stat.decision = "Exclude";
					stat.reason = jsondata.excludeReason || "AEA 1954";
				}
				else if (jsondata.refer) {
					stat.decision = "Refer";
					stat.reason = jsondata.referto || "";
				} else {
					stat.decision = "None";
					stat.reason = "";
				}
			});
			if (stat.reviewDate) {
				stat.taskResult = stat.reviewDate + ': ' + stat.decision + ', ' + stat.reason;
			}
		} else if ('afdoTaskData' == taskDataForm) {
			dataset.forEach((ds: any) => {
				let jsondata = JSON.parse(ds.data);
				stat.reviewDate = jsondata.reviewDate;
				stat.decision = jsondata.decision || 'None';
				stat.reason = jsondata.reason || 'None';
				stat.referto = jsondata.referto;
				stat.nextReview = jsondata.nextReview;
			});
			if (stat.reviewDate) {
				stat.taskResult = stat.reviewDate + ': ' + stat.decision + (stat.referto ? ' ' + stat.referto : '') + ', ' + stat.reason;
			}
		}
		else {
			// detailed task results are not relevant
			stat.taskResult = (dataset[0]) ? (dataset[0].reviewDecision) : 'Completed';
			stat.taskResult = stat.taskResult.toLowerCase();
		}
	};
}
